import { Label } from "layout/typography/Label";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { useState, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useListModelsQuery } from "store/queries/chat";

import { Integrations } from "./Integrations";
import { OrdersVisibility } from "./OrdersVisibility";
import { PaymentOptions } from "./PaymentOptions";

export function FeatureFlags() {
  const {
    control,
    trigger,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<any>();
  const [expanded, setExpanded] = useState(false);
  const { data: chatModels } = useListModelsQuery();

  const patientChatBotVersions =
    chatModels
      ?.filter((model) => model.type === "PatientChatBot")
      ?.map((model) => ({
        label: model.name,
        value: model.id,
      })) || [];

  const hasPatientChatBotOptions = patientChatBotVersions.length > 0;

  const contractManagementBotVersions =
    chatModels
      ?.filter((model) => model.type === "ContractManagementBot")
      ?.map((model) => ({
        label: model.name,
        value: model.id,
      })) || [];

  const hasContractManagementBotOptions =
    contractManagementBotVersions.length > 0;

  return (
    <div className="block bg-white border-round-xl border-1 border-gray-300 p-4">
      <button
        type="button"
        className="flex align-items-center justify-content-between w-full border-none bg-transparent p-0 cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="text-black font-bold text-lg m-0">Feature Flags</span>
        <i
          className={`pi pi-chevron-down text-gray-600 transition-duration-200 transition-all ${
            expanded ? "rotate-180" : ""
          }`}
          style={{ fontSize: "0.75rem" }}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="flex flex-column gap-4 mt-4">
          <div className="flex gap-6">
            <div className="flex gap-3">
              <Controller
                name="eligibilityEnabled"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="eligibilityEnabled"
                    checked={value}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">Eligibility</span>
                <span className="text-sm text-gray-500">
                  Enable eligibility checks
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-column gap-4 border-bottom-1 border-gray-200 pb-4">
            <span className="font-medium text-sm mb-2 text-gray-600 ">
              ELIGIBILITY BENEFITS RULES
            </span>
            <div className="flex gap-3">
              <Controller
                name="facilityRules"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="facilityRules"
                    checked={value}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">Facility</span>
                <span className="text-sm text-gray-500">
                  Enable facility rules
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <Controller
                name="nonFacilityRules"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="nonFacilityRules"
                    checked={value}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">Non-Facility</span>
                <span className="text-sm text-gray-500">
                  Enable non-facility rules
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-column gap-4 border-bottom-1 border-gray-200 pb-4">
            <div className="flex gap-3">
              <Controller
                name="directPayEnabled"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="directPayEnabled"
                    checked={value}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">Direct Pay</span>
                <span className="text-sm text-gray-500">
                  Enable direct pay functionality
                </span>
              </div>
            </div>
            <div className="flex flex-column gap-3">
              <div className="flex gap-3">
                <Controller
                  name="patientChatBotEnabled"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputSwitch
                      inputId="patientChatBotEnabled"
                      checked={!!value}
                      disabled={!hasPatientChatBotOptions}
                      onChange={(e) => {
                        onChange(e.value);
                        if (e.value && patientChatBotVersions.length > 0) {
                          setValue(
                            "patientChatBotId",
                            patientChatBotVersions[0].value
                          );
                        } else {
                          setValue("patientChatBotId", null);
                        }
                      }}
                    />
                  )}
                />
                <div className="flex flex-column">
                  <span className="font-medium text-sm mb-1">
                    Patient Chat Bot Enabled
                  </span>
                  <span className="text-sm text-gray-500">
                    Enable patient chat bot
                  </span>
                </div>
              </div>
              {watch("patientChatBotEnabled") && (
                <div className="flex flex-column gap-1 ml-7">
                  <Label htmlFor="patientChatBotId">
                    Patient Chat Bot Version
                  </Label>
                  <Controller
                    name="patientChatBotId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Dropdown
                        id="patientChatBotId"
                        value={value}
                        onChange={onChange}
                        options={patientChatBotVersions}
                        placeholder="Select version"
                        className="w-full md:w-3"
                      />
                    )}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-column gap-3">
              <div className="flex gap-3">
                <Controller
                  name="contractManagementChatBotEnabled"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputSwitch
                      inputId="contractManagementChatBotEnabled"
                      checked={!!value}
                      disabled={!hasContractManagementBotOptions}
                      onChange={(e) => {
                        onChange(e.value);
                        if (
                          e.value &&
                          contractManagementBotVersions.length > 0
                        ) {
                          setValue(
                            "contractManagementChatBotId",
                            contractManagementBotVersions[0].value
                          );
                        } else {
                          setValue("contractManagementChatBotId", null);
                        }
                      }}
                    />
                  )}
                />
                <div className="flex flex-column">
                  <span className="font-medium text-sm mb-1">
                    Contract Management Chat Bot Enabled
                  </span>
                  <span className="text-sm text-gray-500">
                    Enable contract management chat bot
                  </span>
                </div>
              </div>
              {watch("contractManagementChatBotEnabled") && (
                <div className="flex flex-column gap-1 ml-7">
                  <Label htmlFor="contractManagementChatBotId">
                    Contract Management Chat Bot Version
                  </Label>
                  <Controller
                    name="contractManagementChatBotId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Dropdown
                        id="contractManagementChatBotId"
                        value={value}
                        onChange={onChange}
                        options={contractManagementBotVersions}
                        placeholder="Select version"
                        className="w-10rem"
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </div>
          <PaymentOptions />
          <Integrations />
          <OrdersVisibility />
        </div>
      )}
    </div>
  );
}
